import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, FileText, User, Calendar, CheckCircle, Eye, Download, FileDown } from 'lucide-react';
import { CollaboratorInvoice } from '../../types/collaborators';
import { collaboratorInvoicesService } from '../../services/supabaseService';
import { notificationService } from '../../services/notificationService';
import InvoiceModal from './InvoiceModal';
import ExportInvoicesModal from './ExportInvoicesModal';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/textUtils';

const InvoicesList: React.FC = () => {
  const [invoices, setInvoices] = useState<CollaboratorInvoice[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<CollaboratorInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'received' | 'validated' | 'paid' | 'rejected'>('all');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { isAdmin, isCollaboradorProgramador } = usePermissions();
  const isProgrammerView = isCollaboradorProgramador();

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando facturas desde Supabase...');
      const invoicesData = await collaboratorInvoicesService.getAll();
      setInvoices(invoicesData);
      console.log('✅ Facturas cargadas:', invoicesData.length);
    } catch (error) {
      console.error('Error loading invoices:', error);
      alert('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = () => {
    setEditingInvoice(null);
    setIsModalOpen(true);
  };

  const handleEditInvoice = (invoice: CollaboratorInvoice) => {
    setEditingInvoice(invoice);
    setIsModalOpen(true);
  };

  const handleSaveInvoice = (invoiceData: Partial<CollaboratorInvoice> & { _resubmit?: boolean }) => {
    if (editingInvoice) {
      // Update existing (incl. resubmit del colaborador)
      updateInvoice(invoiceData);
    } else {
      // Create new
      createInvoice(invoiceData);
    }
  };

  const createInvoice = async (invoiceData: Partial<CollaboratorInvoice>) => {
    try {
      console.log('🔄 Creando factura en Supabase...');
      const newInvoice = await collaboratorInvoicesService.create({
        userId: invoiceData.collaboratorId, // collaboratorId es realmente userId
        invoiceNumber: invoiceData.invoiceNumber,
        date: invoiceData.date,
        amount: invoiceData.amount,
        currency: invoiceData.currency,
        attachmentUrl: invoiceData.attachmentUrl,
        attachmentName: invoiceData.attachmentName,
        status: invoiceData.status || 'received',
        timesheetIds: invoiceData.timesheetIds || []
      });

      await loadInvoices();
      setIsModalOpen(false);
      setEditingInvoice(null);
      alert('✅ Factura creada correctamente');

      const collabName = invoiceData.collaboratorName || 'El colaborador';
      await notificationService.createNotification({
        code: 'INVOICE.CREATED',
        title: 'Factura recibida',
        body: `${collabName} ha enviado la factura ${invoiceData.invoiceNumber} · ${Number(invoiceData.amount || 0)} ${invoiceData.currency || 'EUR'}`,
        severity: 'info',
        data: {
          invoice_id: newInvoice.id,
          invoice_number: invoiceData.invoiceNumber,
          collaborator_name: collabName,
        },
      });

      // Digest diario por email retirado 2026-09-18 (queue nunca se
      // procesaba). Admin recibe notif in-app arriba.
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('❌ Error al crear la factura');
    }
  };

  const updateInvoice = async (invoiceData: Partial<CollaboratorInvoice> & { _resubmit?: boolean; rejectionReason?: string | null }) => {
    if (!editingInvoice) return;

    try {
      console.log('🔄 Actualizando factura en Supabase...');
      await collaboratorInvoicesService.update(editingInvoice.id, {
        userId: (invoiceData as any).collaboratorId,
        invoiceNumber: invoiceData.invoiceNumber,
        date: invoiceData.date,
        amount: invoiceData.amount,
        currency: invoiceData.currency,
        attachmentUrl: invoiceData.attachmentUrl,
        attachmentName: invoiceData.attachmentName,
        status: invoiceData.status,
        timesheetIds: invoiceData.timesheetIds,
        rejectionReason: invoiceData.rejectionReason,
      });

      await loadInvoices();
      setIsModalOpen(false);
      setEditingInvoice(null);
      alert(invoiceData._resubmit
        ? '✅ Factura reenviada al administrador'
        : '✅ Factura actualizada correctamente');

      // Nombre del colaborador dueño para meterlo en el body.
      const editingAny = editingInvoice as any;
      const collabName = editingAny?.userName || editingInvoice.collaboratorName || 'El colaborador';

      // En resubmit del colaborador → broadcast a admin. En update
      // normal, notif de actualización.
      await notificationService.createNotification({
        code: invoiceData._resubmit ? 'INVOICE.CREATED' : 'INVOICE.UPDATED',
        title: invoiceData._resubmit ? 'Factura reenviada' : 'Factura actualizada',
        body: invoiceData._resubmit
          ? `${collabName} ha corregido y reenviado la factura ${invoiceData.invoiceNumber}`
          : `Se actualizó la factura ${invoiceData.invoiceNumber} de ${collabName}`,
        severity: 'info',
        data: {
          invoice_id: editingInvoice.id,
          invoice_number: invoiceData.invoiceNumber,
          collaborator_name: collabName,
        },
      });
    } catch (error: any) {
      console.error('Error updating invoice:', error);
      alert('❌ Error al actualizar la factura: ' + (error?.message || error?.details || JSON.stringify(error)));
    }
  };

  const handleValidateInvoice = async (invoiceId: string) => {
    try {
      console.log('🔄 Validando factura:', invoiceId);
      const { supabase } = await import('../../services/supabaseService');

      // Obtener datos de la factura antes de validar
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) {
        throw new Error('Factura no encontrada');
      }

      // Obtener nombre del usuario actual
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const { data: currentUserData } = await supabase
        .from('users')
        .select('name')
        .eq('id', currentUser?.id)
        .maybeSingle();

      const validatorName = currentUserData?.name || 'Administrador';
      const validatedAt = new Date();

      await collaboratorInvoicesService.update(invoiceId, {
        status: 'validated',
        validatedBy: validatorName,
        validatedAt: validatedAt
      });

      await loadInvoices();
      alert('✅ Factura validada correctamente');

      await notificationService.createNotification({
        code: 'INVOICE.VALIDATED',
        title: 'Factura Validada',
        body: `La factura ${invoice.invoiceNumber} ha sido validada`,
        severity: 'info',
        data: { invoice_id: invoiceId }
      });

      // Enviar email inmediato al colaborador
      try {
        const { emailNotificationService } = await import('../../services/emailNotificationService');

        // Obtener información del colaborador
        const { data: collaborator } = await supabase
          .from('users')
          .select('id, name, email')
          .eq('id', invoice.collaboratorId)
          .single();

        if (collaborator) {
          await emailNotificationService.sendInvoiceValidatedEmail(
            collaborator.name,
            collaborator.email,
            collaborator.id,
            invoice.invoiceNumber,
            invoice.amount,
            invoice.currency,
            validatorName,
            validatedAt,
            invoiceId,
            invoice.attachmentUrl
          );
          console.log('✅ Email de validación enviado a:', collaborator.email);
        }
      } catch (emailError) {
        console.error('❌ Error enviando email de validación:', emailError);
      }
    } catch (error: any) {
      console.error('Error validating invoice:', error);
      alert('❌ Error al validar la factura: ' + (error?.message || error?.details || JSON.stringify(error)));
    }
  };

  const handleRejectInvoice = async (invoice: CollaboratorInvoice, reason: string) => {
    try {
      const { supabase } = await import('../../services/supabaseService');

      await collaboratorInvoicesService.update(invoice.id, {
        status: 'rejected' as any,
        rejectionReason: reason,
      } as any);

      await loadInvoices();

      // Notificación al colaborador dueño de la factura.
      try {
        await notificationService.createNotification({
          code: 'INVOICE.REJECTED',
          title: 'Factura rechazada',
          body: `Tu factura ${invoice.invoiceNumber} ha sido rechazada: ${reason}`,
          severity: 'warn',
          user_id: (invoice as any).userId || invoice.collaboratorId,
          data: { invoice_id: invoice.id, invoice_number: invoice.invoiceNumber, reason },
        });
      } catch (notifErr) {
        console.warn('No se pudo notificar rechazo:', notifErr);
      }

      // Email al colaborador con motivo del rechazo.
      try {
        const { data: collaborator } = await supabase
          .from('users')
          .select('id, name, email')
          .eq('id', (invoice as any).userId || invoice.collaboratorId)
          .single();

        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const { data: adminData } = await supabase
          .from('users')
          .select('name')
          .eq('id', currentUser?.id)
          .maybeSingle();
        const rejectedBy = adminData?.name || 'Administrador';

        if (collaborator?.email) {
          const { emailNotificationService } = await import('../../services/emailNotificationService');
          await emailNotificationService.sendInvoiceRejectedEmail(
            collaborator.name,
            collaborator.email,
            collaborator.id,
            invoice.invoiceNumber,
            invoice.amount,
            invoice.currency,
            reason,
            rejectedBy,
            invoice.id
          );
          console.log('✅ Email de rechazo enviado a:', collaborator.email);
        }
      } catch (emailErr) {
        console.warn('No se pudo enviar email de rechazo:', emailErr);
      }

      alert('Factura rechazada. Se ha notificado al colaborador.');
    } catch (error: any) {
      console.error('Error rechazando factura:', error);
      alert('❌ Error al rechazar la factura: ' + (error?.message || error?.details || JSON.stringify(error)));
      throw error;
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      console.log('🔄 Marcando factura como pagada:', invoiceId);
      const { supabase, paymentsService } = await import('../../services/supabaseService');

      // Obtener datos de la factura antes de marcar como pagada
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) {
        throw new Error('Factura no encontrada');
      }

      const paidAt = new Date();
      const invoiceAny = invoice as any;
      const userId = invoiceAny.userId || invoice.collaboratorId;
      const userName = invoiceAny.userName || invoice.collaboratorName;

      // Actualizar factura a pagada
      await collaboratorInvoicesService.update(invoiceId, {
        status: 'paid'
      });

      // Crear registro de pago automáticamente
      try {
        console.log('📝 Creando registro de pago con datos:', {
          invoiceId: invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          userId: userId,
          userName: userName,
          paymentDate: paidAt,
          amount: invoice.amount,
          currency: invoice.currency,
          paymentMethod: 'Transferencia bancaria',
          status: 'executed',
          approvalStatus: 'approved'
        });

        await paymentsService.create({
          invoiceId: invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          userId: userId,
          userName: userName,
          paymentDate: paidAt,
          amount: invoice.amount,
          currency: invoice.currency,
          paymentMethod: 'Transferencia bancaria',
          status: 'executed',
          approvalStatus: 'approved',
          notes: 'Pago registrado automáticamente al marcar factura como pagada'
        });
        console.log('✅ Registro de pago creado automáticamente');
      } catch (paymentError) {
        console.error('❌ Error creating payment record:', paymentError);
        alert('⚠️ Factura marcada como pagada pero hubo un error al crear el registro de pago: ' + paymentError);
      }

      await loadInvoices();
      alert('✅ Factura marcada como pagada y pago registrado');

      await notificationService.createNotification({
        code: 'INVOICE.PAID',
        title: 'Factura Pagada',
        body: `La factura ${invoice.invoiceNumber} ha sido marcada como pagada`,
        severity: 'info',
        data: { invoice_id: invoiceId }
      });

      // Enviar email inmediato al colaborador
      try {
        const { emailNotificationService } = await import('../../services/emailNotificationService');

        // Obtener información del colaborador
        const { data: collaborator } = await supabase
          .from('users')
          .select('id, name, email')
          .eq('id', invoice.collaboratorId)
          .single();

        if (collaborator) {
          await emailNotificationService.sendInvoicePaidEmail(
            collaborator.name,
            collaborator.email,
            collaborator.id,
            invoice.invoiceNumber,
            invoice.amount,
            invoice.currency,
            paidAt,
            'Transferencia bancaria',
            invoiceId
          );
          console.log('✅ Email de pago enviado a:', collaborator.email);
        }
      } catch (emailError) {
        console.error('❌ Error enviando email de pago:', emailError);
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      alert('❌ Error al marcar la factura como pagada');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (invoice && window.confirm(`¿Eliminar la factura "${invoice.invoiceNumber}"? Esta acción no se puede deshacer.`)) {
      try {
        console.log('🔄 Eliminando factura:', invoiceId);
        const invoice = invoices.find(i => i.id === invoiceId);
        await collaboratorInvoicesService.delete(invoiceId);
        await loadInvoices();
        alert('✅ Factura eliminada correctamente');

        await notificationService.createNotification({
          code: 'INVOICE.DELETED',
          title: 'Factura Eliminada',
          body: `La factura ${invoice?.invoiceNumber} ha sido eliminada`,
          severity: 'warn',
          data: { invoice_number: invoice?.invoiceNumber }
        });
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('❌ Error al eliminar la factura');
      }
    }
  };

  const getStatusColor = (status: CollaboratorInvoice['status']) => {
    switch (status) {
      case 'received': return 'bg-blue-100 text-blue-800';
      case 'validated': return 'bg-yellow-100 text-yellow-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: CollaboratorInvoice['status']) => {
    switch (status) {
      case 'received': return 'Recibida';
      case 'validated': return 'Validada';
      case 'paid': return 'Pagada';
      case 'rejected': return 'Rechazada';
      default: return status;
    }
  };

  const filteredInvoices = filter === 'all' 
    ? invoices 
    : invoices.filter(i => i.status === filter);

  const totalAmount = filteredInvoices.reduce((sum, i) => sum + i.amount, 0);
  const pendingAmount = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando facturas...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Facturas de Plantilla</h2>
          <p className="text-gray-600">Gestión de facturas recibidas de colaboradores externos</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            <span>Exportar Facturas</span>
          </button>
          <button
            onClick={handleCreateInvoice}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Registrar Factura</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filtrar por estado:</span>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'Todas' },
              { value: 'received', label: 'Recibidas' },
              { value: 'validated', label: 'Validadas' },
              { value: 'paid', label: 'Pagadas' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value as any)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === option.value
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Facturas</p>
              <p className="text-2xl font-bold text-blue-900">{filteredInvoices.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Importe Total</p>
              <p className="text-2xl font-bold text-green-900">{totalAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Pendientes de Pago</p>
              <p className="text-2xl font-bold text-yellow-900">{pendingAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Eye className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Pagadas</p>
              <p className="text-2xl font-bold text-purple-900">
                {invoices.filter(i => i.status === 'paid').length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Importe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Archivo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{(invoice as any).userName || invoice.collaboratorName || 'Sin nombre'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {invoice.date.toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {invoice.attachmentUrl ? (
                      <a
                        href={invoice.attachmentUrl}
                        download={invoice.attachmentName || 'factura.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <Download className="w-3 h-3" />
                        <span>Descargar</span>
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400">Sin archivo</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {invoice.status === 'received' && (
                        <button
                          onClick={() => handleValidateInvoice(invoice.id)}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Validar factura"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {invoice.status === 'validated' && (
                        <button
                          onClick={() => handleMarkAsPaid(invoice.id)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Marcar como pagada"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Editar factura"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Eliminar factura"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay facturas</h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' 
                ? 'Comienza registrando la primera factura de colaborador'
                : `No hay facturas con estado "${getStatusLabel(filter as any)}"`
              }
            </p>
            {filter === 'all' && (
              <button
                onClick={handleCreateInvoice}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Registrar Factura</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      <InvoiceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingInvoice(null);
        }}
        onSave={handleSaveInvoice}
        invoice={editingInvoice}
        onValidate={async (inv) => {
          await handleValidateInvoice(inv.id);
        }}
        onReject={async (inv, reason) => {
          await handleRejectInvoice(inv, reason);
        }}
      />

      {/* Export Modal */}
      <ExportInvoicesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};

export default InvoicesList;